<template>
  <div>
    <div v-if="$store.getters.getLoadingStationAddEdit" class="afdc-large-spinner-container">
      <div class="afdc-large-spinner"></div>
    </div>

    <template v-if="formSubmitted && !$store.getters.getStationAddEditError">
      <div v-if="formValidated" class="afdc-alert afdc-alert-success">
        {{ $t('Thank you for your station submission. Please note that your update will not automatically appear in the Alternative Fueling Station Locator. All station information must be confirmed before changes are made. The turn-around time for updates will depend on the completeness of the information provided as well as the responsiveness of the station or point of contact.')}}
        <span v-if="comments === 'specific'">{{ $t('We will contact you directly if we have any questions about your submission.') }} </span>
      </div>

      <div v-else class="afdc-alert afdc-alert-danger">
        {{ $t('This form cannot be submitted. See the errors marked below.') }}
      </div>
    </template>

    <div v-if="$store.getters.getStationAddEditError" class="afdc-alert afdc-alert-warning">
      <em>{{ $store.getters.getStationAddEditError }}</em>
    </div>

    <div class="afdc-alert afdc-alert-warning" v-if="formData.fuels && formData.fuels.includes('ELEC') && formData.ev_network && (formData.ev_network != 'Non-Networked')">
      <div v-html="$sprintf($t('Data for this station comes from the %s network. You can use this form to suggest updates, but we also recommend that you <a href=\'%s\'>contact the network</a> directly to report changes because we cannot edit station information for this network.'), evNetwork(formData.ev_network), formData.ev_network_web)"></div>
    </div>

    <div class="afdc-col-sm-12 afdc-specific-radio" v-if="(!formValidated || $store.getters.getStationAddEditError) && this.$route.name ==='StationEdit'">
      <p>{{ $t('What feedback would you like to provide?') }}</p>
      <form>
        <div class="afdc-custom-control afdc-custom-radio afdc-custom-control-inline">
          <input type="radio" id="afdc-edit-specific" value="specific" v-model="comments" name="comment-question" class="afdc-custom-control-input">
          <label class="afdc-custom-control-label" for="afdc-edit-specific">{{ $t('Specific station details') }}</label>
        </div>
        <div class="afdc-custom-control afdc-custom-radio afdc-custom-control-inline">
          <input type="radio" id="afdc-edit-general" value="general" v-model="comments" name="comment-question" class="afdc-custom-control-input">
          <label class="afdc-custom-control-label" for="afdc-edit-general">{{ $t('General comments') }}</label>
        </div>
      </form>
    </div>

    <form v-if="!formValidated || $store.getters.getStationAddEditError" @submit.prevent="validateBeforeSubmit" id="afdc-station-form">
      <input type="hidden" v-model="formData.id" name="id">

      <div v-if="comments === 'specific'">
        <p><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> <small>{{ $t('Required field') }}</small></p>

        <h3>{{ $t('Your Contact Information') }}</h3>
        <div class="afdc-row">
          <div class="afdc-col-md-6">
            <div class="afdc-form-group afdc-row">
              <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-user-first-name"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ $t('First Name') }}</label>
              <div class="afdc-col-md-8">
                <input type="text" class="afdc-form-control" id="afdc-user-first-name" v-model="formData.first_name" v-validate="checkRequired" name="first_name">
                <span v-show="errors.has('first_name')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
              </div>
            </div>
          </div>

          <div class="afdc-col-md-6">
            <div class="afdc-form-group afdc-row">
              <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-user-last-name"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ $t('Last Name') }}</label>
              <div class="afdc-col-md-8">
                <input type="text" class="afdc-form-control" id="afdc-user-last-name" v-model="formData.last_name" v-validate="checkRequired" name="last_name">
                <span v-show="errors.has('last_name')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="afdc-row">
          <div class="afdc-col-md-6">
            <div class="afdc-form-group afdc-row">
              <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-user-email"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ $t('Email') }}</label>
              <div class="afdc-col-md-8">
                <input type="text" class="afdc-form-control" id="afdc-user-email" v-model="formData.email" v-validate="'required|email'" name="email" data-vv-validate-on="blur">
                <span v-show="errors.has('email')" class="afdc-input-error">{{ $t('A valid email address is required.') }}</span>
              </div>
            </div>
          </div>

          <div class="afdc-col-md-6">
            <div class="afdc-form-group afdc-row">
              <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-user-phone"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ $t('Phone') }}</label>
              <div class="afdc-col-md-8">
                <input type="text" class="afdc-form-control" id="afdc-user-phone" v-model="formData.phone" v-validate="checkRequired" name="phone">
                <span v-show="errors.has('phone')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
              </div>
            </div>
          </div>
        </div>

        <template v-if="$store.getters.localeCountry !== 'CA'">
          <div class="afdc-form-group afdc-row">
            <label class="afdc-col-md-6 afdc-col-form-label" for="afdc-user-coalition"> {{ $t('If you are part of Clean Cities, indicate which coalition.') }}</label>
            <div class="afdc-col-md-6">
              <select class="afdc-custom-select" id="afdc-user-coalition" v-model="formData.coalition" name="coalition">
                <option value=""></option>
                <option v-for="option in coalitions" v-bind:key="option.id" v-bind:value="option.name">
                  {{ option.name }}
                </option>
              </select>
            </div>
          </div>

          <div class="afdc-alert afdc-alert-warning" v-if="formData.coalition" v-html="$sprintf($t('If you have more than one station to add or update, use the Station Update Spreadsheet in the <a href=\'%s\'>Coordinator Toolbox</a>.'), 'https://cleancities.energy.gov/toolbox/alternative-fuel-station-report/')"></div>
        </template>

        <h3>{{ $t('Station Information') }}</h3>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-name"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr>{{ $t('Station Name') }}</label>
          <div class="afdc-col-md-8">
            <input type="text" class="afdc-form-control" id="afdc-station-name" v-model="formData.station_name" v-validate="'required'" name="station_name">
            <span v-show="errors.has('station_name')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ $t('Available Fuel(s)') }}</label>
          <div class="afdc-col-md-8">
            <template v-for="(fuel, code) in $store.getters.loaderFuelNamesByKey">
              <div v-if="code !== 'all'" v-bind:key="code" class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station">
                <input type="checkbox" v-bind:value="code" v-model="formData.fuels" v-bind:id="`afdc-station-fuels-${code}`" class="afdc-custom-control-input" v-validate="'required'" name="fuels">
                <label class="afdc-custom-control-label" v-bind:for="`afdc-station-fuels-${code}`">{{ fuel }}</label>
              </div>
            </template>
            <span v-show="errors.has('fuels')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
          </div>
        </div>

        <br>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-country"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ $t('Country') }}</label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-station-country" v-model="formData.country" v-validate="'required'" name="country">
              <option value=""></option>
              <option v-for="option in countryOptions" v-bind:key="option.key" v-bind:value="option.key">
                {{ option.name }}
              </option>
            </select>
          </div>
          <div class="afdc-col-md-5">
            <span v-show="errors.has('country')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-address"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ $t('Address') }}</label>
          <div class="afdc-col-md-8">
            <input type="text" class="afdc-form-control" id="afdc-station-address" v-model="formData.street_address" v-validate="'required'" name="street_address">
            <span v-show="errors.has('street_address')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-city"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ $t('City') }}</label>
          <div class="afdc-col-md-5">
            <input type="text" class="afdc-form-control" id="afdc-station-city" v-model="formData.city" v-validate="'required'" name="city">
            <span v-show="errors.has('city')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-state"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ formData.country === 'CA' ? $t('Province/Territory') : $t('State') }}</label>
          <div class="afdc-col-md-2">
            <input type="text" class="afdc-form-control" id="afdc-station-state" v-model="formData.state" v-validate="'required'" name="state">
          </div>
          <div class="afdc-col-md-6">
            <span v-show="errors.has('state')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-zip"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ $t('Postal Code') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" class="afdc-form-control" id="afdc-station-zip" v-model="formData.zip" v-validate="'required'" name="zip">
          </div>
          <div class="afdc-col-md-5">
            <span v-show="errors.has('zip')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-access"><abbr v-bind:title="$t('Required')" class="afdc-required">*</abbr> {{ $t('Type of Access') }}</label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-station-access" v-model="formData.access" v-validate="'required'" name="access">
              <option value=""></option>
              <option value="Public">{{ $t('Public') }}</option>
              <option value="Private">{{ $t('Private') }}</option>
            </select>
          </div>
          <div class="afdc-col-md-5">
            <span v-show="errors.has('access')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
          </div>
        </div>

        <h3>{{ $t('Optional Station Information') }}</h3>
        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-web-form-status">{{ $t('Status') }}</label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-web-form-status" v-model="formData.web_form_status" name="web_form_status">
              <option value=""></option>
              <option value="existing">{{ $t('Existing') }}</option>
              <option value="planned">{{ $t('Planned') }}</option>
              <option value="temporarily_unavailable">{{ $t('Temporarily Unavailable') }}</option>
              <option value="out_of_service">{{ $t('Out of Service') }}</option>
            </select>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.web_form_status === 'existing' || formData.web_form_status === 'temporarily_unavailable'">
          <label class="afdc-col-md-4 afdc-col-form-label" for="datepicker-element-open">{{ $t('Open Date') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" id="open-date-picker" class="afdc-form-control" v-model="formData.open_date" key="open-date"/>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.web_form_status && (formData.web_form_status === 'planned' || formData.web_form_status === 'temporarily_unavailable')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="datepicker-element-open">{{ $t('Expected Open/Reopen Date') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" id="expected-date-picker" class="afdc-form-control" v-model="formData.expected_date" key="expected-date" />
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.web_form_status && formData.web_form_status === 'out_of_service'">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-service-description">{{ $t('Indicate when the station went out of service and why it closed') }}</label>
          <div class="afdc-col-md-8">
            <input type="text" class="afdc-form-control" id="afdc-station-service-description" v-model="formData.service_description" name="service_description">
            <span class="afdc-form-hint">{{ $t('In addition to marking the station as out of service on this form, please report any broken equipment or access issues to the station owner.') }}</span>
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label">{{ $t('Payment Types') }}</label>
          <div class="afdc-col-md-8">
            <template v-for="(payment, code) in $store.getters.loaderPaymentMethodNamesByKey">
              <div v-if="code !== 'all'" v-bind:key="code" class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station">
                <input type="checkbox" v-bind:value="code" v-model="formData.payments" v-bind:id="`afdc-station-payments-${code}`" class="afdc-custom-control-input" name="payments">
                <label class="afdc-custom-control-label" v-bind:for="`afdc-station-payments-${code}`">{{ payment }}</label>
              </div>
            </template>
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-phone">{{ $t('Station Phone') }}</label>
          <div class="afdc-col-md-8">
            <input type="text" class="afdc-form-control" id="afdc-station-phone" v-model="formData.station_phone" name="station_phone">
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-hours">{{ $t('Business Hours') }}</label>
          <div class="afdc-col-md-8">
            <input type="text" class="afdc-form-control" id="afdc-station-hours" v-model="formData.access_days_time" name="access_days_time">
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-latitude">{{ $t('Latitude') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" class="afdc-form-control" id="afdc-station-latitude" v-model="formData.latitude" name="latitude">
          </div>
        </div>

        <div class="afdc-form-group afdc-row">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-longitude">{{ $t('Longitude') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" class="afdc-form-control" id="afdc-station-longitude" v-model="formData.longitude" name="longitude">
          </div>
        </div>

        <h3 v-show="formData.fuels.length > 0">{{ $t('Optional Fuel Information') }}</h3>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('BD')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-bd-blends">{{ $t('Biodiesel Blends Available') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" class="afdc-form-control" id="afdc-station-bd-blends" v-model="formData.bd_blends" name="bd_blends">
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('CNG')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-cng-pressure">{{ $t('CNG Fill Pressure (psi)') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" class="afdc-form-control" id="afdc-station-cng-pressure" v-model="formData.ng_psi" name="ng_psi">
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('CNG')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-cng-type">{{ $t('CNG Fill Type') }}</label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-station-cng-type" v-model="formData.ng_fill_type_code" name="ng_fill_type_code">
              <option value=""></option>
              <option value="Q">{{ $t('Fast-fill') }}</option>
              <option value="T">{{ $t('Time-fill') }}</option>
              <option value="B">{{ $t('Both: fast-fill and time-fill') }}</option>
            </select>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('CNG')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-cng-dispenser">{{ $t('CNG Dispensers Installed') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" class="afdc-form-control" id="afdc-station-cng-dispenser" v-model="formData.cng_dispenser_num" name="cng_dispenser_num">
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('CNG')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-cng-compression">{{ $t('CNG Capacity per Compressor (standard cubic feet per minute)') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" class="afdc-form-control" id="afdc-station-cng-compression" v-model="formData.cng_total_compression" name="cng_compression">
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('CNG')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-cng-total-storage">{{ $t('CNG Total Storage Capacity (standard cubic feet)') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" class="afdc-form-control" id="afdc-station-cng-total-storage" v-model="formData.cng_total_storage" name="cng_storage">
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('CNG')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-cng-renewable-source">{{ $t('CNG Renewable Energy Used') }}</label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-station-cng-renewable-source" v-model="formData.cng_renewable_source" name="cng_renewable_source">
              <option value=""></option>
                <template v-for="(source, code) in $store.getters.loaderRenewableSourceNamesByKey">
                  <option v-bind:value="code" v-bind:key="code">{{ $t(source) }}</option>
                </template>
            </select>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('ELEC')">
          <div class="afdc-col-md-7">
            <div class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station">
              <input type="checkbox" v-model="formData.ev_levels" value="level1" id="afdc-station-level-1" class="afdc-custom-control-input" name="ev_levels">
              <label class="afdc-custom-control-label" for="afdc-station-level-1">{{ $t('Level 1 electric vehicle charging available') }}</label>
            </div>
          </div>

          <div class="afdc-col-md-5" v-show="formData.fuels && formData.fuels.includes('ELEC')">
            <div class="afdc-form-group afdc-row">
              <label class="afdc-col-md-8 afdc-col-form-label" for="afdc-station-level-1-count">{{ $t('Number of Level 1 Ports') }}</label>
              <div class="afdc-col-md-4">
                <input type="text" class="afdc-form-control" id="afdc-station-level-1-count" v-model="formData.ev_level1_evse_num" name="ev_level1_evse_num">
              </div>
            </div>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('ELEC')">
          <div class="afdc-col-md-7">
            <div class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station">
              <input type="checkbox" v-model="formData.ev_levels" value="level2" id="afdc-station-level-2" class="afdc-custom-control-input" name="ev_levels">
              <label class="afdc-custom-control-label" for="afdc-station-level-2">{{ $t('Level 2 electric vehicle charging available') }}</label>
            </div>
          </div>

          <div class="afdc-col-md-5" v-show="formData.fuels && formData.fuels.includes('ELEC')">
            <div class="afdc-form-group afdc-row">
              <label class="afdc-col-md-8 afdc-col-form-label" for="afdc-station-level-2-count">{{ $t('Number of Level 2 Ports') }}</label>
              <div class="afdc-col-md-4">
                <input type="text" class="afdc-form-control" id="afdc-station-level-2-count" v-model="formData.ev_level2_evse_num" name="ev_level2_evse_num">
              </div>
            </div>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('ELEC')">
          <div class="afdc-col-md-7">
            <div class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station">
              <input type="checkbox" v-model="formData.ev_levels" value="dc_fast" id="afdc-station-dc-fast" class="afdc-custom-control-input" name="ev_levels">
              <label class="afdc-custom-control-label" for="afdc-station-dc-fast">{{ $t('DC fast charging available') }}</label>
            </div>
          </div>

          <div class="afdc-col-md-5">
            <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('ELEC')">
              <label class="afdc-col-md-8 afdc-col-form-label" for="afdc-station-dc-fast-count">{{ $t('Number of DC Fast Ports') }}</label>
              <div class="afdc-col-md-4">
                <input type="text" class="afdc-form-control" id="afdc-station-dc-fast-count" v-model="formData.ev_dc_fast_num" name="ev_dc_fast_num">
              </div>
            </div>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('ELEC')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-electric-pricing">{{ $t('Electric Charging Pricing') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" class="afdc-form-control" id="afdc-station-electric-pricing" v-model="formData.ev_pricing" name="cng_storage">
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('ELEC')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-ev-renewable-source">{{ $t('Electric Charging Renewable Energy Used') }}</label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-station-ev-renewable-source" v-model="formData.ev_renewable_source" name="ev_renewable_source">
              <option value=""></option>
                <template v-for="(source, code) in $store.getters.loaderRenewableSourceNamesByKey">
                  <option v-bind:value="code" v-bind:key="code">{{ $t(source) }}</option>
                </template>
            </select>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('ELEC')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-ev-network">{{ $t('Electric Charging Network') }}</label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-station-ev-network" v-model="formData.ev_network" name="ev_network">
                <option value=""></option>
                <option v-for="networkOption in stationEvNetworks(networkOptions)" :value="networkOption.key" :key="networkOption.key">{{ networkOption.name }} </option>
            </select>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('ELEC')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-elec-manufacturer">{{ $t('Electric Charging Manufacturer') }}</label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-station-elec-manufacturer" v-model="formData.post_manufacturer" name="post_manufacturer">
              <option value=""></option>
                <template v-for="(source, code) in $store.getters.postManufacturerNamesByKey">
                  <option v-bind:value="code" v-bind:key="code">{{ source }}</option>
                </template>
            </select>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('ELEC')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-electric-charging">{{ $t('Electric Charging Power (kW)') }}</label>
          <div class="afdc-col-md-3">
            <input type="text" class="afdc-form-control" id="afdc-station-electric-charging" v-model="formData.ev_charging_power" name="electric charging power">
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('E85')">
          <label class="afdc-col-md-4 afdc-col-form-label">{{ $t('Ethanol Other Blends Offered') }}</label>
          <div class="afdc-col-md-8">
            <template v-for="(blend, code) in $store.getters.loaderEthanolBlendNamesByKey">
              <div v-bind:key="code" class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station">
                <input type="checkbox" v-bind:value="code" v-model="formData.e85_other_ethanol_blends" v-bind:id="`afdc-station-ethanol-blend-${code}`" class="afdc-custom-control-input" name="ethanol_blends">
                <label class="afdc-custom-control-label" v-bind:for="`afdc-station-ethanol-blend-${code}`">{{ blend }}</label>
              </div>
            </template>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('HY')">
          <label class="afdc-col-md-4 afdc-col-form-label">{{ $t('Hydrogen Pressure') }}</label>
          <div class="afdc-col-md-8">
            <template v-for="(pressure, code) in $store.getters.loaderHydrogenPressureNamesByKey">
              <div v-bind:key="code" class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station">
                <input type="checkbox" v-bind:value="code" v-model="formData.hy_pressures" v-bind:id="`afdc-station-hy-pressure-${code}`" class="afdc-custom-control-input" name="hy_pressure">
                <label class="afdc-custom-control-label" v-bind:for="`afdc-station-hy-pressure-${code}`">{{ pressure }} {{ $t('bar') }}</label>
              </div>
            </template>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('HY')">
          <label class="afdc-col-md-4 afdc-col-form-label">{{ $t('Hydrogen SAE Standards Compliance') }}</label>
          <div class="afdc-col-md-8">
            <template v-for="(standard, code) in $store.getters.loaderHydrogenStandardNamesByKey">
              <div v-bind:key="code" class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station">
                <input type="checkbox" v-bind:value="code" v-model="formData.hy_standards" v-bind:id="`afdc-station-hy-standard-${code}`" class="afdc-custom-control-input" name="hy_standards">
                <label class="afdc-custom-control-label" v-bind:for="`afdc-station-hy-standard-${code}`">{{ $t('SAE') }} {{ standard }}</label>
              </div>
            </template>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('LPG')">
          <label class="afdc-col-md-4 afdc-col-form-label">{{ $t('Propane Nozzles') }}</label>
          <div class="afdc-col-md-8">
            <template v-for="(nozzle, code) in $store.getters.loaderLpgNozzleTypeNamesByKey">
              <div v-bind:key="code" class="afdc-custom-control afdc-custom-checkbox afdc-custom-checkbox-station">
                <input type="checkbox" v-bind:value="code" v-model="formData.lpg_nozzle_types" v-bind:id="`afdc-station-lpg-nozzle-${code}`" class="afdc-custom-control-input" name="lpg_nozzle">
                <label class="afdc-custom-control-label" v-bind:for="`afdc-station-lpg-nozzle-${code}`">{{ nozzle }}</label>
              </div>
            </template>
          </div>
        </div>

        <div class="afdc-form-group afdc-row" v-show="formData.fuels && formData.fuels.includes('LNG')">
          <label class="afdc-col-md-4 afdc-col-form-label" for="afdc-station-lng-renewable-source">{{ $t('LNG Renewable Energy Used') }}</label>
          <div class="afdc-col-md-3">
            <select class="afdc-custom-select" id="afdc-station-lng-renewable-source" v-model="formData.lng_renewable_source" name="lng_renewable_source">
              <option value=""></option>
                <template v-for="(source, code) in $store.getters.loaderRenewableSourceNamesByKey">
                  <option v-bind:value="code" v-bind:key="code">{{ $t(source) }}</option>
                </template>
            </select>
          </div>
        </div>
      </div>

      <div class="afdc-form-group">
        <h3 v-if="comments === 'specific'"><label class="afdc-col-form-label" for="afdc-station-comments">{{ $t('Comments') }}</label></h3>
        <h3 v-else><label class="afdc-col-form-label" for="afdc-station-comments"><abbr v-bind:title="$t('Comments')" class="afdc-required">*</abbr>{{ $t('Comments') }}</label></h3>
        <textarea type="text" class="afdc-form-control" id="afdc-station-comments" v-model="formData.comments" v-validate="requiredGeneral" name="comments"></textarea>
        <span v-show="errors.has('comments')" class="afdc-input-error">{{ $t('This field is required.') }}</span>
      </div>

      <button type="submit" class="afdc-btn afdc-btn-success afdc-float-right">{{ $t('Submit') }}</button>
    </form>
  </div>
</template>

<script>
import Vue from 'vue';
import handleErrors from '@/lib/handleErrors';
import reject from 'lodash/reject';
import Flatpickr from 'flatpickr';

export default {
  data() {
    return {
      formData: {
        country: null,
        fuels: [],
        payments: [],
        ev_levels: [],
        hy_pressures: [],
        hy_standards: [],
        e85_other_ethanol_blends: [],
        lpg_nozzle_types: [],
        open_date: '',
        expected_date: '',
        coalition: '',
        web_form_status: '',
      },
      formSubmitted: false,
      formValidated: false,
      formError: false,
      comments: 'specific',
      coalitions: null,
    };
  },
  computed: {
    countryOptions() {
      return reject(this.$store.getters.loaderCountries, { key: 'all' });
    },

    networkOptions() {
      return reject(this.$store.getters.loaderElecNetworks, { key: 'all' });
    },

    checkRequired() {
      return this.comments === 'specific' ? 'required' : '';
    },
    requiredGeneral() {
      return this.comments === 'general' ? 'required' : '';
    },
  },
  mounted() {
    Flatpickr('#open-date-picker', {
      static: true,
      dateFormat: 'Y-m-d',
    });

    Flatpickr('#expected-date-picker', {
      dateFormat: 'Y-m-d',
      static: true,
    });
    Vue.axios
      .get('https://cleancities.energy.gov/api/v1/coalitions')
      .then((response) => {
        this.coalitions = response.data.result.sort((a, b) => ((a.name > b.name) ? 1 : -1));
      });
  },
  watch: {
    '$store.getters.stationDetails': {
      immediate: true,
      handler: 'copyStationData',
    },
    comments(newVal) {
      if (this.$route.name === 'StationEdit' && newVal === 'general') {
        this.formData.email = 'webmaster.afdc@nrel.gov';
        this.formData.comment_note = 'Only general comments were submitted. No changes were made to the edit form.';
      } else {
        this.formData.email = '';
        this.formData.comment_note = '';
      }
    },
  },
  methods: {
    copyStationData() {
      if (this.$route.name === 'StationEdit') {
        const station = this.$store.getters.stationDetails;
        this.formData.id = station.id;
        this.formData.fuels = [station.fuel_type_code];
        this.formData.country = station.country;
        this.formData.street_address = station.street_address;
        this.formData.city = station.city;
        this.formData.state = station.state;
        this.formData.zip = station.zip;
        this.formData.access = this.accessValue(station.groups_with_access_code);
        this.formData.station_name = station.station_name;
        this.formData.web_form_status = station.web_form_status;
        this.formData.open_date = station.open_date;
        this.formData.expected_date = station.expected_date;
        this.formData.payments = station.cards_accepted ? station.cards_accepted.split(' ') : [];
        this.formData.station_phone = station.station_phone;
        this.formData.access_days_time = this.$store.getters.localeLang === 'fr' ? station.access_days_time_fr : station.access_days_time;
        this.formData.latitude = station.latitude;
        this.formData.longitude = station.longitude;
        this.formData.bd_blends = this.$store.getters.localeLang === 'fr' ? station.bd_blends_fr : station.bd_blends;
        this.formData.ng_psi = station.ng_psi;
        this.formData.cng_total_compression = station.cng_total_compression;
        this.formData.cng_total_storage = station.cng_total_storage;
        this.formData.cng_dispenser_num = station.cng_dispenser_num;
        this.formData.ng_fill_type_code = station.ng_fill_type_code;
        this.formData.cng_renewable_source = station.cng_renewable_source;
        this.formData.ev_network = station.ev_network;
        this.formData.ev_network_web = station.ev_network_web;
        this.formData.ev_level1_evse_num = station.ev_level1_evse_num;
        this.formData.ev_level2_evse_num = station.ev_level2_evse_num;
        this.formData.ev_dc_fast_num = station.ev_dc_fast_num;
        this.formData.ev_pricing = station.ev_pricing;
        this.formData.ev_charging_power = station.ev_charging_power;
        this.formData.ev_renewable_source = station.ev_renewable_source;
        this.formData.post_manufacturer = station.post_manufacturer;
        this.formData.lng_renewable_source = station.lng_renewable_source;
        this.formData.hy_pressures = station.hy_pressures ? station.hy_pressures : [];
        this.formData.hy_standards = station.hy_standards ? station.hy_standards : [];
        this.formData.e85_other_ethanol_blends = station.e85_other_ethanol_blends ? station.e85_other_ethanol_blends : [];
        this.formData.lpg_nozzle_types = station.lpg_nozzle_types ? station.lpg_nozzle_types : [];

        if (station.ev_level1_evse_num && station.ev_level1_evse_num > 0) {
          this.formData.ev_levels.push('level1');
        }
        if (station.ev_level2_evse_num && station.ev_level2_evse_num > 0) {
          this.formData.ev_levels.push('level2');
        }
        if (station.ev_dc_fast_num && station.ev_dc_fast_num > 0) {
          this.formData.ev_levels.push('dc_fast');
        }

        // Clone the initial form data onto the original_data attribute, which
        // the API will use for generating diffs of what changed.
        this.formData.original_data = { ...this.formData };
      }
    },

    evNetwork(code) {
      return this.$store.getters.loaderElecNetworksByKey[code];
    },

    accessValue(code) {
      if (code && code.includes('Public')) {
        return 'Public';
      }
      if (code && code.includes('Private')) {
        return 'Private';
      }
      return null;
    },

    validateBeforeSubmit() {
      document.getElementById('afdc-alt-fuel-station').scrollTop = 0;
      this.$store.commit('setLoadingStationAddEdit', true);

      this.$validator.validateAll().then((result) => {
        if (result) {
          Vue.axios.request({
            url: `${process.env.VUE_APP_API_URL_ROOT}/v1/locator/${this.$route.name === 'StationEdit' ? 'update_station_mail' : 'create_station_mail'}.json`,
            method: this.$route.name === 'StationEdit' ? 'put' : 'post',
            params: {
              api_key: window.afdcStationsOptions.apiKey,
            },
            data: JSON.stringify({
              ...this.formData,
              locale: this.$store.getters.locale,
            }),
            headers: {
              'Content-Type': 'application/json',
            },
          })
            .then(() => {
              this.$store.commit('setLoadingStationAddEdit', false);
              this.$store.commit('setStationAddEditError', null);
              this.formSubmitted = true;
              this.formValidated = true;
            })
            .catch((e) => {
              this.$store.commit('setLoadingStationAddEdit', false);
              this.$store.commit('setStationAddEditError', handleErrors(e));
              this.formSubmitted = false;
              this.formValidated = true;
            });
        } else {
          this.$store.commit('setLoadingStationAddEdit', false);
          this.$store.commit('setStationAddEditError', null);
          this.formSubmitted = true;
          this.formValidated = false;
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/_mixins.scss';

@include media-breakpoint-up(md) {
  .col-form-label {
    text-align: right;
  }
}

.custom-select {
  option {
    color:#495057;
  }
  optgroup {
    color:#495057;
  }
}
.input-error {
  color: #a94442;
}

abbr.required {
  font-weight: bold;
  color: #e87723;
  cursor: inherit;
  border-style: none;
  text-decoration: none;
}

.specific-radio {
  background-color: #f6f6f6;
  padding: 15px;
  margin-bottom: 15px;
}

.custom-checkbox-station {
  background-color: #f6f6f6;
  display: block;
  border-bottom: 1px solid #ddd;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  &:hover {
    background-color: #eee;
  }

  .custom-control-label {
    display: block;
    padding: 10px 25px;
    &:hover {
      cursor: pointer;
    }
  }

  .custom-control-label::before {
    width: 28px;
    height: 28px;
    margin-left: 10px;
    top: 9px;
    border-radius: 50%;
    background-color: #ddd;
    &:hover {
      cursor: pointer;
    }
  }

  .custom-control-label::after {
    width: 28px;
    height: 28px;
    margin-left: 10px;
    top: 9px;
  }

  .custom-control-input {
    margin-right: 20px;
  }

  .custom-control-input:checked ~ .custom-control-label::before {
    background-color: #66bb6a;
  }
}

  .flatpickr-input {
    background-color: #fff !important;
  }

 .form-hint {
    font-size: 14px;
    font-style: italic;
  }
</style>
